import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Spinner, Alert, Modal, ListGroup, InputGroup } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { cookie } from 'utils/cookies';

import {
    FETCH_STATION_WISE_FORMS,
    DOWNLOAD_CHECKLIST_FORM,
    STATUS_CHANGE,
    CREATE_CHECKLIST,
    DELETE_CHECKLIST_FORM,
    ASSIGN_TO_CONTRACTOR, // ✅ NEW
} from 'actions/formActions';
import { FaDownload, FaPen, FaSpinner } from 'react-icons/fa';

// ✅ If your project uses a different action/selector for listing users,
//    change FETCH_USERS + the selector below accordingly.
import { FETCH_USERS } from 'actions/userActions'; // 🔁 Adjust if needed

const StationForms = () => {
    const { stationId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        loading,
        stationwiseformslist = [],
        downloadLoading,
        error,
        statusLoading,
        deleteLoading,
    } = useSelector((state) => state.checklist);

    const { users } = useSelector((state) => state.users);

    const contractors =
        Array.isArray(users)
            ? users.filter((u) => (u?.role || '').toLowerCase() === 'contractor')
            : [];

    const [search, setSearch] = useState('');
    const [formFilter, setFormFilter] = useState('');
    const [downloadInProgress, setDownloadInProgress] = useState(null);
    const [statusChangeInProgress, setStatusChangeInProgress] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedFormId, setSelectedFormId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [formName, setFormName] = useState('');
    const [showAddFormsModal, setShowAddFormsModal] = useState(false);
    const [quantities, setQuantities] = useState({});
    const [addFormError, setAddFormError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(null);

    // ✅ NEW: contractor modal state
    const [showContractorModal, setShowContractorModal] = useState(false);
    const [contractorLevel, setContractorLevel] = useState(null); // 'l1' | 'l2' | 'root'
    const [selectedContractorId, setSelectedContractorId] = useState('');
    const [assignInProgress, setAssignInProgress] = useState(false);

    const userRole = cookie.get('user-role');

    const formNameToComponent = {
        db_panel: 'DBPanelForm',
        apfc_panel: 'APFCPanelForm',
        cabel_tray: 'CabelTrayForm',
        earthing: 'EarthingForm',
        ht_cable: 'HTCableForm',
        lightning_arrester: 'LightningArresterForm',
        lt_cable: 'LTCableForm',
        lt_panel: 'LTPanelForm',
        transformer: 'TransformerForm',
        vcb: 'VCBForm',
        hoto: 'HOTOForm',
    };

    const checklistItems = [
        'APFC Panel',
        'Cabel Tray',
        'DB Panel',
        'Earthing',
        'HT Cabel',
        'Lightning Arrester',
        'LT Cabel',
        'LT Panel',
        'Transformer',
        'VCB',
        'HOTO (Project Handover Takeover Report)',
    ];

    const formNames = Object.keys(formNameToComponent);

    const prettifyFormName = (name = '') =>
        String(name).replace(/\(.*?\)/g, '').replace(/_/g, ' ').trim().toUpperCase();

    const statusLabel = (status = '') => {
        switch ((status || '').toLowerCase()) {
            case 'approved':
                return 'APPROVED';
            case 'rejected':
                return 'REJECTED';
            case 're-work':
            case 'rework':
                return 'RE-WORK';
            case 'submitted':
            case 'draft':
                return 'DRAFT';
            case 'pending':
                return 'PENDING';
            case 'contractor':
                return 'CONTRACTOR';
            default:
                return (status || 'N/A').toUpperCase();
        }
    };

    const statusVariant = (status = '') => {
        switch ((status || '').toLowerCase()) {
            case 'approved':
                return 'success';
            case 'rejected':
                return 'danger';
            case 're-work':
            case 'rework':
                return 'warning';
            case 'submitted':
            case 'draft':
                return 'primary';
            case 'pending':
                return 'secondary';
            case 'contractor':
                return 'info';
            default:
                return 'secondary';
        }
    };

    const fetchStationWiseForms = () => {
        if (stationId) {
            dispatch({ type: FETCH_STATION_WISE_FORMS, payload: stationId });
        }
    };

    useEffect(() => {
        fetchStationWiseForms();
        setQuantities(checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {}));
    }, [dispatch, stationId, statusLoading]); // keep your existing deps

    const initiateStatusChange = (formId, formName, newStatus) => {
        setSelectedFormId(formId);
        setSelectedStatus(newStatus);
        setFormName(formName);
        setShowStatusModal(true);
    };

    const handleStatusChange = () => {
        if (selectedFormId) {
            setStatusChangeInProgress(selectedFormId);
            dispatch({
                type: STATUS_CHANGE,
                payload: { formId: selectedFormId, status: selectedStatus, formName },
            });
            if (statusLoading) {
                setStatusChangeInProgress(null);
                toast.success(`Form status changed to ${selectedStatus}`);
                setShowStatusModal(false);
            }
        }
    };

    const initiateDeleteForm = (formId, formName) => {
        setSelectedFormId(formId);
        setFormName(formName);
        setShowDeleteModal(true);
    };

    const handleDeleteForm = () => {
        if (selectedFormId) {
            setDeleteInProgress(selectedFormId);
            dispatch({
                type: DELETE_CHECKLIST_FORM,
                payload: { formId: selectedFormId, formName },
            });
            if (!deleteLoading) {
                fetchStationWiseForms({});
                setShowDeleteModal(false);
            }
        }
    };

    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setSelectedFormId(null);
        setSelectedStatus('');
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedFormId(null);
        setFormName('');
    };

    const handleAddFormsModalOpen = () => {
        setShowAddFormsModal(true);
        setAddFormError('');
    };

    const handleAddFormsModalClose = () => {
        setShowAddFormsModal(false);
        setQuantities(checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {}));
        setAddFormError('');
    };

    const handleQuantityChange = (item, value) => {
        const qty = parseInt(value, 10) || 0;
        setQuantities((prev) => ({ ...prev, [item]: qty }));
        if (addFormError) setAddFormError('');
    };

    const adjustQuantity = (item, delta) => {
        setQuantities((prev) => {
            const updated = Math.max(0, (prev[item] || 0) + delta);
            return { ...prev, [item]: updated };
        });
    };

    const validateAddForms = () => {
        const totalSelected = Object.values(quantities).reduce((sum, qty) => sum + (qty > 0 ? 1 : 0), 0);
        if (totalSelected === 0) {
            setAddFormError('Select at least one form with quantity greater than 0.');
            return false;
        }
        return true;
    };

    const handleAddFormsSubmit = () => {
        if (!validateAddForms()) return;

        const checklist = {};
        Object.entries(quantities).forEach(([item, quantity]) => {
            if (quantity > 0) {
                const key = item
                    .toLowerCase()
                    .replace(/\(.*?\)/g, '')
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_|_$/g, '');
                checklist[key] = quantity;
            }
        });

        const payload = {
            stationId: stationId.toUpperCase(),
            project_name: stationwiseformslist[0]?.project_name || 'Unknown',
            checklist,
            is_existing_station: true,
        };

        dispatch({
            type: CREATE_CHECKLIST,
            payload,
            cb: (res) => {
                if (res.status === 200) {
                    toast.success('Forms added successfully');
                    fetchStationWiseForms();
                    handleAddFormsModalClose();
                } else {
                    setAddFormError(res.data?.message || 'Failed to add forms');
                }
            },
        });
    };

    useEffect(() => {
        if (!downloadLoading) {
            setDownloadInProgress(null);
        }
    }, [downloadLoading]);

    const filteredForms = Array.isArray(stationwiseformslist)
        ? stationwiseformslist.filter((f) => {
            if (!f || typeof f !== 'object') return false;

            const formNameMatch =
                f.formName?.toLowerCase().includes(search.toLowerCase()) ||
                f.userName?.toLowerCase().includes(search.toLowerCase());

            const formFilterMatch = !formFilter || f.formName?.toLowerCase() === formFilter.toLowerCase();

            const statusMatch = !statusFilter || (f.status || '').toLowerCase() === statusFilter.toLowerCase();

            const formDate = new Date(f.createdAt);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;

            const dateInRange =
                (!start || formDate >= start) && (!end || formDate <= new Date(end.setHours(23, 59, 59, 999)));

            return formNameMatch && formFilterMatch && statusMatch && dateInRange;
        })
        : [];

    const sortedForms = [...filteredForms].sort((a, b) => {
        const aTime = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bTime = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return bTime - aTime;
    });

    const handleDownload = (formId, formName, userName) => {
        if (formId) {
            setDownloadInProgress(formId);
            dispatch({
                type: DOWNLOAD_CHECKLIST_FORM,
                payload: { formId, formName, userName, stationId },
                meta: { formId },
            });
        }
    };

    const formatToIST = (utcDate) => {
        if (!utcDate) return 'N/A';
        const date = new Date(utcDate);
        return date.toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
    };

    // ✅ NEW: open contractor modal and fetch contractor list
    const openContractorModal = (form, level /* 'l1' | 'l2' | 'root' */) => {
        setSelectedFormId(form._id);
        setFormName(form.formName);
        setContractorLevel(level);
        setSelectedContractorId('');
        setShowContractorModal(true);

        // Fetch contractors (role filter) – adjust action name/shape if needed
        dispatch({
            type: FETCH_USERS,
            payload: { role: 'contractor', limit: 100, page: 1 }, // 🔁 tune as per your API
        });
    };

    // ✅ NEW: assign to contractor
    const handleAssignToContractor = () => {
        if (!selectedContractorId) {
            toast.error('Please select a contractor');
            return;
        }
        setAssignInProgress(true);
        dispatch({
            type: ASSIGN_TO_CONTRACTOR,
            payload: {
                formId: selectedFormId,
                formName,
                level: contractorLevel, // 'l1' | 'l2' | 'root'
                contractorId: selectedContractorId,
                stationId,
            },
            cb: (res) => {
                console.log("-====", res)
                setAssignInProgress(false);
                if (res?.status === 200) {
                    toast.success('Assigned to contractor');
                    setShowContractorModal(false);
                    fetchStationWiseForms();
                } else {
                    toast.error(res?.data?.message || 'Failed to assign');
                }
            },
        });
    };

    const renderLevelCell = (levelObj, form, levelKey /* 'l1' | 'l2' | 'root' */) => {
        const status = (levelObj?.status || '').toLowerCase();

        // If CONTRACTOR → show button only if user is not contractor
        if (status === 'contractor') {
            const isAssigned = levelObj?.contractorId;
            return (
                <div className="d-flex align-items-center gap-2">
                    <span className={`badge bg-${statusVariant('contractor')}`}>{statusLabel('contractor')}</span>
                    {userRole !== 'contractor' && (
                        <Button
                            size="sm"
                            variant={isAssigned ? "secondary" : "outline-primary"}
                            onClick={() => openContractorModal(form, levelKey)}
                        >
                            {isAssigned ? "Assigned" : "Send to Contractor"}
                        </Button>
                    )}
                </div>
            );
        }

        // Original rendering fallback
        if (status === 'draft') return 'draft';
        if (status === 'approved') return 'approved';
        return levelObj?.status ? `Pending from ${levelObj.status}` : 'N/A';
    };

    return (
        <div className="page-body">
            <div className="container-xl new_application">
                <div className="row mb-3">
                    <div className="col">
                        <h2 className="page-title">Forms for Station: {stationId}</h2>
                    </div>
                    <div className="col-auto">
                        <Button variant="primary" onClick={handleAddFormsModalOpen}>
                            Add Forms
                        </Button>
                    </div>
                </div>

                {error && (
                    <Alert variant="danger">Error fetching forms: {error.message || 'Unknown error'}</Alert>
                )}

                <div className="row mb-3">
                    <div className="col-md-3">
                        <Form.Control
                            type="text"
                            placeholder="Search by User or Form Name"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3">
                        <Form.Select value={formFilter} onChange={(e) => setFormFilter(e.target.value)}>
                            <option value="">Filter by Form Name</option>
                            {formNames.map((name, idx) => (
                                <option key={idx} value={name}>
                                    {prettifyFormName(name)}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                    <div className="col-md-2">
                        <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="submitted">Draft</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="re-work">Re-Work</option>
                            <option value="contractor">Contractor</option>
                        </Form.Select>
                    </div>
                    <div className="col-md-2">
                        <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                        <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center my-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Table bordered hover responsive>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Form Name</th>
                                <th>Version</th>
                                <th>User</th>
                                <th>Status</th>
                                <th>L1 Status</th>
                                <th>L2 Status</th>
                                <th>Created At</th>
                                <th>Updated At</th>
                                <th>Action</th>
                                <th>Download PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedForms.length > 0 ? (
                                sortedForms.map((form) => (
                                    <tr key={form._id}>
                                        <td className="text-uppercase">{form?._id?.slice(-6)}</td>
                                        <td className="text-uppercase">{prettifyFormName(form?.formName || 'N/A')}</td>
                                        <td>{form?.version || 'N/A'}</td>
                                        <td>{form?.userName || 'N/A'}</td>
                                        <td>
                                            {form.l1 ? (
                                                <span className={`badge bg-${statusVariant(form.status)}`}>{statusLabel(form.status)}</span>
                                            ) : (
                                                renderLevelCell(form, form, 'root')
                                            )}
                                        </td>

                                        {/* ✅ L1 & L2 cells updated */}
                                        <td>{form.l1 ? renderLevelCell(form.l1, form, 'l1') : 'N/A'}</td>
                                        <td>{form.l2 ? renderLevelCell(form.l2, form, 'l2') : 'N/A'}</td>

                                        <td>{formatToIST(form?.createdAt)}</td>
                                        <td>{formatToIST(form?.updatedAt)}</td>
                                        <td>
                                            {form?.status !== 'approved' && form?.status !== 'rejected' && (
                                                <Button
                                                    variant={form?.status === 'pending' ? 'secondary' : 'primary'}
                                                    size="sm"
                                                    onClick={() =>
                                                        navigate(`/formList/${stationId}/${form?.formName}/${form._id}`)
                                                    }
                                                    disabled={!form._id}
                                                    className="me-2"
                                                >
                                                    <FaPen size={20} />
                                                </Button>
                                            )}

                                            {userRole === 'engineer' && (
                                                <>
                                                    {form?.status === 'approved' && (
                                                        <Button variant="success" size="sm" disabled className="me-2">
                                                            Approved
                                                        </Button>
                                                    )}
                                                    {form?.status === 'rejected' && (
                                                        <Button variant="danger" size="sm" disabled className="me-2">
                                                            Rejected
                                                        </Button>
                                                    )}
                                                    {form?.status === 're-work' && (
                                                        <Button variant="warning" size="sm" disabled className="me-2">
                                                            Rework
                                                        </Button>
                                                    )}
                                                    {form?.status === 'pending' && (
                                                        <>
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() =>
                                                                    initiateStatusChange(form._id, form.formName, 'approved')
                                                                }
                                                                className="me-2"
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() =>
                                                                    initiateStatusChange(form._id, form.formName, 'rejected')
                                                                }
                                                                className="me-2"
                                                            >
                                                                Reject
                                                            </Button>
                                                            <Button
                                                                variant="warning"
                                                                size="sm"
                                                                onClick={() =>
                                                                    initiateStatusChange(form._id, form.formName, 're-work')
                                                                }
                                                                className="me-2"
                                                            >
                                                                Rework
                                                            </Button>
                                                        </>
                                                    )}
                                                    {
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => initiateDeleteForm(form._id, form.formName)}
                                                            disabled={deleteInProgress === form._id}
                                                            className="me-2"
                                                        >
                                                            Delete
                                                        </Button>
                                                    }
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            <Button
                                                variant="info"
                                                size="sm"
                                                onClick={() =>
                                                    handleDownload(form._id, form?.formName, form?.userName)
                                                }
                                                disabled={!form._id || downloadInProgress === form._id}
                                                className="me-2"
                                            >
                                                {downloadInProgress === form._id ? (
                                                    <FaSpinner size={20} />
                                                ) : (
                                                    <FaDownload size={20} />
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="11" className="text-center text-muted">
                                        No forms available for this station.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}

                {/* Status Change Confirmation Modal */}
                <Modal show={showStatusModal} onHide={handleCloseStatusModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Status Change</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>Are you sure you want to {statusLabel(selectedStatus)} this form?</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseStatusModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleStatusChange}
                            disabled={statusChangeInProgress === selectedFormId}
                        >
                            {statusChangeInProgress === selectedFormId ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Changing...
                                </>
                            ) : (
                                'Confirm'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{`Are you sure you want to delete the form "${formName}"?`}</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseDeleteModal}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteForm} disabled={deleteInProgress === selectedFormId}>
                            {deleteInProgress === selectedFormId ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ✅ NEW: Send to Contractor Modal */}
                <Modal show={showContractorModal} onHide={() => setShowContractorModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Contractor Assignment</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-2">
                            <strong>Form:</strong> {prettifyFormName(formName)} &nbsp;|&nbsp; <strong>Level:</strong>{' '}
                            {contractorLevel === 'root' ? 'ROOT' : String(contractorLevel || '').toUpperCase()}
                        </div>
                        {(() => {
                            // Find the current form
                            const currentForm = stationwiseformslist.find(f => f._id === selectedFormId);
                            if (!currentForm) return <div>Form not found</div>;

                            const levelData = contractorLevel === 'root' ? currentForm : currentForm[contractorLevel];
                            const isAssigned = levelData?.contractorId;
                            const assignedContractor = isAssigned ? contractors.find(c => (c.id || c._id) === levelData.contractorId) : null;
                            const assignedAt = levelData?.contractorAssignedAt ? formatToIST(levelData.contractorAssignedAt) : null;

                            if (isAssigned && assignedContractor) {
                                return (
                                    <div>
                                        <h5>Already Assigned</h5>
                                        <p><strong>Contractor:</strong> {assignedContractor.name || `${assignedContractor.firstName || ''} ${assignedContractor.lastName || ''}`.trim()}</p>
                                        <p><strong>Email:</strong> {assignedContractor.email}</p>
                                        {assignedAt && <p><strong>Assigned At:</strong> {assignedAt}</p>}
                                    </div>
                                );
                            } else {
                                return (
                                    <Form.Group>
                                        <Form.Label>Select Contractor</Form.Label>
                                        <Form.Select
                                            value={selectedContractorId}
                                            onChange={(e) => setSelectedContractorId(e.target.value)}
                                        // disabled={usersLoading}
                                        >
                                            <option value="">{'Choose a contractor'}</option>
                                            {contractors.map((u) => (
                                                <option key={u.id || u._id} value={u.id || u._id}>
                                                    {u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.email})
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Only users with role <code>contractor</code> are listed.
                                        </Form.Text>
                                    </Form.Group>
                                );
                            }
                        })()}
                    </Modal.Body>
                    <Modal.Footer>
                        {(() => {
                            const currentForm = stationwiseformslist.find(f => f._id === selectedFormId);
                            const levelData = contractorLevel === 'root' ? currentForm : currentForm?.[contractorLevel];
                            const isAssigned = levelData?.contractorId;

                            if (!isAssigned) {
                                return (
                                    <>
                                        <Button variant="danger" onClick={() =>
                                            navigate(`/usermanagement/add`)
                                        }>
                                            Add New Contractor
                                        </Button>
                                        <Button variant="secondary" onClick={() => setShowContractorModal(false)}>
                                            Cancel
                                        </Button>
                                        <Button variant="primary" onClick={handleAssignToContractor} disabled={assignInProgress || !selectedContractorId}>
                                            {assignInProgress ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Sending...
                                                </>
                                            ) : (
                                                'Send'
                                            )}
                                        </Button>
                                    </>
                                );
                            } else {
                                return (
                                    <Button variant="secondary" onClick={() => setShowContractorModal(false)}>
                                        Close
                                    </Button>
                                );
                            }
                        })()}
                    </Modal.Footer>
                </Modal>

                {/* Add Forms Modal (unchanged) */}
                <Modal show={showAddFormsModal} onHide={handleAddFormsModalClose} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Add Forms to Station: {stationId}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {addFormError && (
                            <Alert variant="danger" className="mb-3">
                                {addFormError}
                            </Alert>
                        )}
                        <ListGroup>
                            {checklistItems.map((item) => (
                                <ListGroup.Item key={item} className="d-flex align-items-center">
                                    <span className="flex-grow-1">{item}</span>
                                    <InputGroup style={{ width: '150px' }}>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => adjustQuantity(item, -1)}
                                            disabled={quantities[item] === 0}
                                        >
                                            −
                                        </Button>
                                        <Form.Control
                                            type="number"
                                            value={quantities[item]}
                                            min={0}
                                            onChange={(e) => handleQuantityChange(item, e.target.value)}
                                            style={{ textAlign: 'center' }}
                                        />
                                        <Button variant="outline-secondary" onClick={() => adjustQuantity(item, 1)}>
                                            +
                                        </Button>
                                    </InputGroup>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleAddFormsModalClose}>
                            Cancel
                        </Button>
                        <Button variant="success" onClick={handleAddFormsSubmit}>
                            Send
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default StationForms;
